import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { showToast } from "../../../components/ClassicToast";
import {
  Search,
  Mail,
  Phone,
  MapPin,
  Loader,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  Check,
  X,
} from "lucide-react";
import {
  lmsGetRegistrationRequests,
  lmsApproveRegistrationRequest,
  lmsRejectRegistrationRequest,
  lmsGetSchoolClasses,
  lmsGetClasses,
  lmsGetStudents,
} from "../api/lmsApi";
import { BASE_URL } from "../../../configs/WebServiceUrl";
import { checkStudentCreationLimit } from "../../../utils/studentLimitUtils";
import "../lms.css";

const getSchoolData = () => {
  try {
    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    const adminId = ud.admin_id || ud.school_id || ud.id || null;
    return { admin_id: adminId, ...ud };
  } catch {
    return { admin_id: null };
  }
};

export default function StudentRequests() {
  const school = getSchoolData();
  const schoolId = school.admin_id;
  const createdBy = school.admin_id;
  const reduxUser = useSelector((state) => state.authSlice?.user);
  const currentPayments = reduxUser?.payments || school?.payments || [];
  const [userPayments, setUserPayments] = useState(
    Array.isArray(currentPayments) && currentPayments.length > 0 ? currentPayments : []
  );
  const [totalStudentsCount, setTotalStudentsCount] = useState(0);

  const [classes, setClasses] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requestSearch, setRequestSearch] = useState("");
  const [requestStatusFilter, setRequestStatusFilter] = useState("");
  const [requestClassFilter, setRequestClassFilter] = useState("");

  // Pagination State
  const [requestPage, setRequestPage] = useState(1);
  const [requestLimit] = useState(10);
  const [requestTotalPages, setRequestTotalPages] = useState(1);
  const [requestTotal, setRequestTotal] = useState(0);
  const [requestOverallTotal, setRequestOverallTotal] = useState(0);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [approvedRequestsCount, setApprovedRequestsCount] = useState(0);
  const [rejectedRequestsCount, setRejectedRequestsCount] = useState(0);

  // Reject Modal State
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedRequestForReject, setSelectedRequestForReject] = useState(null);
  const [rejectionReasonInput, setRejectionReasonInput] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // Load Payments on mount
  useEffect(() => {
    const loadPayments = async () => {
      if (!schoolId) return;
      try {
        const res = await fetch(`${BASE_URL}/api/profile/school-user-details/${schoolId}`);
        const data = await res.json();
        const u = data.user || data;
        if (Array.isArray(u?.payments) && u.payments.length > 0) {
          setUserPayments(u.payments);
        }
      } catch (e) {
        console.error("Error loading user payments:", e);
      }
    };
    loadPayments();
  }, [schoolId]);

  // Load Total Students Count on mount
  useEffect(() => {
    const fetchStudentCount = async () => {
      if (!schoolId) return;
      try {
        const res = await lmsGetStudents({ school_id: schoolId, page: 1, limit: 1 });
        const count = res.data?.total ?? (Array.isArray(res.data?.data) ? res.data.data.length : 0);
        setTotalStudentsCount(count);
      } catch (e) {
        console.error("Error fetching students count:", e);
      }
    };
    fetchStudentCount();
  }, [schoolId]);

  useEffect(() => {
    fetchClasses();
  }, [schoolId]);

  useEffect(() => {
    fetchRegistrationRequests();
  }, [schoolId, requestStatusFilter, requestClassFilter, requestPage, requestSearch]);

  const fetchClasses = async () => {
    try {
      let fetchedClasses = [];
      if (schoolId) {
        const res = await lmsGetSchoolClasses(schoolId);
        const data = res.data?.data || res.data || [];
        if (Array.isArray(data) && data.length > 0) {
          fetchedClasses = data;
        }
      }

      if (fetchedClasses.length === 0) {
        const fallbackRes = await lmsGetClasses();
        fetchedClasses = fallbackRes.data?.data || fallbackRes.data || [];
      }

      setClasses(fetchedClasses);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const fetchRegistrationRequests = async () => {
    setLoadingRequests(true);
    try {
      const params = {
        school_id: schoolId,
        page: requestPage,
        limit: requestLimit,
      };
      if (requestStatusFilter) {
        params.status = requestStatusFilter;
      }
      if (requestClassFilter) {
        params.class_id = requestClassFilter;
      }
      if (requestSearch) {
        params.search = requestSearch;
      }

      const res = await lmsGetRegistrationRequests(params);
      const resData = res.data;
      const list = resData?.data || [];
      const total = resData?.total ?? list.length;
      const pages = resData?.totalPages ?? (Math.ceil(total / requestLimit) || 1);

      setRequests(list);
      setRequestTotal(total);
      setRequestTotalPages(pages);
      setRequestOverallTotal(resData?.overallTotal || total);
      setPendingRequestsCount(resData?.pendingCount || 0);
      setApprovedRequestsCount(resData?.approvedCount || 0);
      setRejectedRequestsCount(resData?.rejectedCount || 0);
    } catch (error) {
      console.error("Error fetching registration requests:", error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleApproveRequest = async (request) => {
    // 1. Check plan limit before confirming / approving
    let currentCount = totalStudentsCount;
    try {
      const resCount = await lmsGetStudents({ school_id: schoolId, page: 1, limit: 1 });
      currentCount = resCount.data?.total ?? totalStudentsCount;
      setTotalStudentsCount(currentCount);
    } catch (e) {
      console.warn("Count fetch error:", e);
    }

    const activePayments = userPayments.length > 0 ? userPayments : currentPayments;
    const { isExceeded, limit } = checkStudentCreationLimit(currentCount, activePayments);

    if (isExceeded) {
      showToast.error(
        `You have exceeded your plan limit (${limit} students). Upgrade your plan to create students.`
      );
      return;
    }

    if (
      !window.confirm(
        `Approve registration for ${request.first_name} ${request.last_name}? This will add them to LMS Students.`
      )
    ) {
      return;
    }

    setActionLoadingId(request.id);
    try {
      const res = await lmsApproveRegistrationRequest(request.id, {
        reviewed_by: createdBy,
      });
      if (res.data?.success || res.status === 200) {
        showToast.success(`Request approved! ${request.first_name} is now enrolled.`);
        setTotalStudentsCount((prev) => prev + 1);
        fetchRegistrationRequests();
      } else {
        showToast.error(res.data?.message || "Failed to approve registration request.");
      }
    } catch (err) {
      console.error("Error approving request:", err);
      showToast.error(err.response?.data?.message || "Error approving registration request.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleOpenRejectModal = (request) => {
    setSelectedRequestForReject(request);
    setRejectionReasonInput("");
    setRejectModalOpen(true);
  };

  const handleCloseRejectModal = () => {
    setSelectedRequestForReject(null);
    setRejectionReasonInput("");
    setRejectModalOpen(false);
  };

  const handleSubmitReject = async () => {
    if (!selectedRequestForReject) return;

    setActionLoadingId(selectedRequestForReject.id);
    try {
      const res = await lmsRejectRegistrationRequest(selectedRequestForReject.id, {
        reviewed_by: createdBy,
        rejection_reason: rejectionReasonInput.trim() || "Application rejected by administrator",
      });
      if (res.data?.success || res.status === 200) {
        showToast.info(
          `Request for ${selectedRequestForReject.first_name} ${selectedRequestForReject.last_name} rejected.`
        );
        handleCloseRejectModal();
        fetchRegistrationRequests();
      } else {
        showToast.error(res.data?.message || "Failed to reject registration request.");
      }
    } catch (err) {
      console.error("Error rejecting request:", err);
      showToast.error(err.response?.data?.message || "Error rejecting registration request.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRequestStatusFilterChange = (e) => {
    setRequestStatusFilter(e.target.value);
    setRequestPage(1);
  };

  const handleRequestClassFilterChange = (e) => {
    setRequestClassFilter(e.target.value);
    setRequestPage(1);
  };

  return (
    <div>
      <style>{`
        .std-compact-stats {
          margin-bottom: 10px !important;
          gap: 10px !important;
        }
        .std-compact-stats .std-stat-card {
          padding: 8px 12px !important;
        }
        .std-compact-stats .std-stat-icon-wrap {
          width: 32px !important;
          height: 32px !important;
        }
        .std-compact-stats .std-stat-label {
          font-size: 11px !important;
        }
        .std-compact-stats .std-stat-value {
          font-size: 15px !important;
        }
        .std-compact-toolbar {
          margin-bottom: 10px !important;
          padding: 6px 10px !important;
        }
        .std-table-compact th {
          padding: 6px 10px !important;
          font-size: 11px !important;
          background: #f8fafc !important;
          white-space: nowrap !important;
          position: sticky;
          top: 0;
          z-index: 2;
        }
        .std-table-compact td {
          padding: 5px 10px !important;
          font-size: 12px !important;
          vertical-align: middle !important;
        }
        .std-table-compact .std-avatar, 
        .std-table-compact .std-avatar-placeholder {
          width: 26px !important;
          height: 26px !important;
          font-size: 11.5px !important;
        }
        .std-table-scroll-container {
          width: 100%;
          overflow-x: auto;
          overflow-y: auto;
          max-height: calc(100vh - 230px);
          -webkit-overflow-scrolling: touch;
        }
      `}</style>

      {/* Stats Cards */}
      <div
        className="std-stats-grid std-compact-stats"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}
      >
        <div className="std-stat-card">
          <div className="std-stat-icon-wrap" style={{ background: "#eff6ff", color: "#1c498d" }}>
            <FileText size={16} />
          </div>
          <div>
            <div className="std-stat-label">Total Applications</div>
            <div className="std-stat-value">{requestOverallTotal}</div>
          </div>
        </div>

        <div className="std-stat-card">
          <div className="std-stat-icon-wrap" style={{ background: "#fef3c7", color: "#b45309" }}>
            <Clock size={16} />
          </div>
          <div>
            <div className="std-stat-label">Pending Review</div>
            <div className="std-stat-value" style={{ color: "#b45309" }}>
              {pendingRequestsCount}
            </div>
          </div>
        </div>

        <div className="std-stat-card">
          <div className="std-stat-icon-wrap" style={{ background: "#ecfdf5", color: "#047857" }}>
            <CheckCircle size={16} />
          </div>
          <div>
            <div className="std-stat-label">Approved</div>
            <div className="std-stat-value" style={{ color: "#047857" }}>
              {approvedRequestsCount}
            </div>
          </div>
        </div>

        <div className="std-stat-card">
          <div className="std-stat-icon-wrap" style={{ background: "#fef2f2", color: "#b91c1c" }}>
            <XCircle size={16} />
          </div>
          <div>
            <div className="std-stat-label">Rejected</div>
            <div className="std-stat-value" style={{ color: "#b91c1c" }}>
              {rejectedRequestsCount}
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar / Filters in One Row */}
      <div
        className="std-toolbar-card"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
          padding: "6px 12px",
          marginBottom: "10px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: "280px" }}>
          <div className="std-search-box" style={{ maxWidth: "320px", flex: 1 }}>
            <input
              type="text"
              className="std-search-input"
              placeholder="Search student, email, mobile..."
              value={requestSearch}
              onChange={(e) => {
                setRequestSearch(e.target.value);
                setRequestPage(1);
              }}
              style={{ padding: "5px 10px", fontSize: "12px" }}
            />
          </div>

          {/* Status Filter */}
          <select
            className="std-filter-select"
            value={requestStatusFilter}
            onChange={handleRequestStatusFilterChange}
            style={{
              padding: "4px 20px 4px 8px",
              fontSize: "12px",
              minWidth: "90px",
              maxWidth: "110px",
              width: "auto",
              backgroundPosition: "right 5px center",
            }}
          >
            <option value="">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>

          {/* Class Filter */}
          <select
            className="std-filter-select"
            value={requestClassFilter}
            onChange={handleRequestClassFilterChange}
            style={{
              padding: "4px 20px 4px 8px",
              fontSize: "12px",
              minWidth: "90px",
              maxWidth: "110px",
              width: "auto",
              backgroundPosition: "right 5px center",
            }}
          >
            <option value="">All Classes</option>
            {classes.map((cls) => {
              const val = cls.class_id || cls.name;
              const label = cls.name || cls.class_name || cls.title || `Class ${cls.id}`;
              return (
                <option key={val} value={val}>
                  {label}
                </option>
              );
            })}
          </select>
        </div>

        <span style={{ fontSize: "11.5px", color: "#64748b", fontWeight: "500", whiteSpace: "nowrap" }}>
          Showing {requests.length} of {requestTotal}
        </span>
      </div>

      {/* Requests Table */}
      <div className="std-table-card">
        <div className="std-table-scroll-container">
          <table className="std-table std-table-compact" style={{ minWidth: "900px" }}>
            <thead>
              <tr>
                <th>Student Details</th>
                <th>Class</th>
                <th>Gender</th>
                <th>Contact Email</th>
                <th>Mobile</th>
                <th>Address</th>
                <th>Date</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loadingRequests ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: "center", padding: "24px" }}>
                    <Loader className="spinner" size={18} style={{ margin: "0 auto 4px" }} />
                    <div style={{ color: "#64748b", fontSize: "12px" }}>Loading requests...</div>
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: "center", padding: "28px 16px" }}>
                    <div
                      style={{
                        width: "38px",
                        height: "38px",
                        borderRadius: "50%",
                        background: "#f1f5f9",
                        color: "#94a3b8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 6px",
                      }}
                    >
                      <Clock size={18} />
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>
                      No registration requests found
                    </div>
                    <div style={{ fontSize: "11.5px", color: "#94a3b8", marginTop: "2px" }}>
                      Share your Coaching Registration link with prospective students.
                    </div>
                  </td>
                </tr>
              ) : (
                requests.map((req) => {
                  const fName = req.first_name || "";
                  const lName = req.last_name || "";
                  const reqStatus = (req.status || "PENDING").toUpperCase();
                  const isPending = reqStatus === "PENDING";
                  const isApproved = reqStatus === "APPROVED";
                  const isRejected = reqStatus === "REJECTED";
                  const formattedDate =
                    req.requested_at || req.created_at
                      ? new Date(req.requested_at || req.created_at).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "N/A";

                  return (
                    <tr key={req.id}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                          <div
                            className="std-avatar-placeholder"
                            style={{
                              background: isApproved ? "#ecfdf5" : isRejected ? "#fef2f2" : "#eff6ff",
                              color: isApproved ? "#047857" : isRejected ? "#b91c1c" : "#1c498d",
                              fontWeight: "700",
                            }}
                          >
                            {(fName[0] || "S").toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: "600", color: "#0f172a", fontSize: "12px" }}>
                              {fName} {lName}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className="std-badge-class" style={{ fontSize: "11px", padding: "2px 6px" }}>
                          {req.class_name ||
                            classes.find((c) => String(c.id) === String(req.class_id))?.name ||
                            `Class ${req.class_id}`}
                        </span>
                      </td>

                      <td>
                        <span style={{ fontSize: "11.5px", color: "#475569", textTransform: "capitalize" }}>
                          {req.gender || "male"}
                        </span>
                      </td>

                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            color: "#475569",
                            fontSize: "11.5px",
                          }}
                        >
                          <Mail size={12} style={{ color: "#94a3b8" }} />
                          {req.email || "N/A"}
                        </div>
                      </td>

                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            color: "#475569",
                            fontSize: "11.5px",
                          }}
                        >
                          <Phone size={12} style={{ color: "#94a3b8" }} />
                          {req.mobile}
                        </div>
                      </td>

                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            color: "#64748b",
                            maxWidth: "140px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "11.5px",
                          }}
                          title={req.address}
                        >
                          <MapPin size={12} style={{ color: "#94a3b8", flexShrink: 0 }} />
                          {req.address || "N/A"}
                        </div>
                      </td>

                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "3px",
                            color: "#64748b",
                            fontSize: "11px",
                          }}
                        >
                          <Calendar size={11} color="#94a3b8" />
                          <span>{formattedDate}</span>
                        </div>
                      </td>

                      <td>
                        {isPending && (
                          <span
                            style={{
                              background: "#fef3c7",
                              color: "#92400e",
                              border: "1px solid #fde68a",
                              padding: "2px 7px",
                              borderRadius: "10px",
                              fontSize: "10.5px",
                              fontWeight: "700",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "3px",
                            }}
                          >
                            <Clock size={10} /> Pending
                          </span>
                        )}
                        {isApproved && (
                          <span
                            style={{
                              background: "#ecfdf5",
                              color: "#047857",
                              border: "1px solid #a7f3d0",
                              padding: "2px 7px",
                              borderRadius: "10px",
                              fontSize: "10.5px",
                              fontWeight: "700",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "3px",
                            }}
                          >
                            <CheckCircle size={10} /> Approved
                          </span>
                        )}
                        {isRejected && (
                          <span
                            style={{
                              background: "#fef2f2",
                              color: "#b91c1c",
                              border: "1px solid #fecaca",
                              padding: "2px 7px",
                              borderRadius: "10px",
                              fontSize: "10.5px",
                              fontWeight: "700",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "3px",
                            }}
                            title={req.rejection_reason || "Rejected"}
                          >
                            <XCircle size={10} /> Rejected
                          </span>
                        )}
                      </td>

                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {isPending ? (
                          <div style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                            <button
                              type="button"
                              onClick={() => handleApproveRequest(req)}
                              disabled={actionLoadingId === req.id}
                              title="Approve and enroll student into LMS"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "3px",
                                padding: "3px 8px",
                                borderRadius: "4px",
                                background: "#ecfdf5",
                                color: "#047857",
                                border: "1px solid #a7f3d0",
                                cursor: "pointer",
                                fontSize: "11px",
                                fontWeight: "600",
                              }}
                            >
                              {actionLoadingId === req.id ? (
                                <Loader size={11} className="spinner" />
                              ) : (
                                <Check size={11} />
                              )}
                              <span>Approve</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => handleOpenRejectModal(req)}
                              disabled={actionLoadingId === req.id}
                              title="Reject request"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "3px",
                                padding: "3px 7px",
                                borderRadius: "4px",
                                background: "#fef2f2",
                                color: "#dc2626",
                                border: "1px solid #fecaca",
                                cursor: "pointer",
                                fontSize: "11px",
                                fontWeight: "600",
                              }}
                            >
                              <X size={11} />
                              <span>Reject</span>
                            </button>
                          </div>
                        ) : isApproved ? (
                          <span style={{ fontSize: "11px", color: "#059669", fontWeight: "600" }}>
                            Enrolled
                          </span>
                        ) : (
                          <span style={{ fontSize: "11px", color: "#94a3b8" }}>Closed</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {requestTotalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 12px",
              borderTop: "1px solid #e2e8f0",
              background: "#f8fafc",
            }}
          >
            <div style={{ fontSize: "11.5px", color: "#64748b" }}>
              Page <strong>{requestPage}</strong> of <strong>{requestTotalPages}</strong> ({requestTotal} Total)
            </div>
            <div style={{ display: "flex", gap: "5px" }}>
              <button
                type="button"
                disabled={requestPage <= 1 || loadingRequests}
                onClick={() => setRequestPage((prev) => Math.max(prev - 1, 1))}
                style={{
                  padding: "3px 10px",
                  borderRadius: "4px",
                  border: "1px solid #cbd5e1",
                  background: requestPage <= 1 ? "#f1f5f9" : "#ffffff",
                  color: requestPage <= 1 ? "#94a3b8" : "#334155",
                  cursor: requestPage <= 1 ? "not-allowed" : "pointer",
                  fontWeight: "500",
                  fontSize: "11.5px",
                }}
              >
                Previous
              </button>
              <button
                type="button"
                disabled={requestPage >= requestTotalPages || loadingRequests}
                onClick={() => setRequestPage((prev) => Math.min(prev + 1, requestTotalPages))}
                style={{
                  padding: "3px 10px",
                  borderRadius: "4px",
                  border: "1px solid #cbd5e1",
                  background: requestPage >= requestTotalPages ? "#f1f5f9" : "#ffffff",
                  color: requestPage >= requestTotalPages ? "#94a3b8" : "#334155",
                  cursor: requestPage >= requestTotalPages ? "not-allowed" : "pointer",
                  fontWeight: "500",
                  fontSize: "11.5px",
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* REJECT REQUEST MODAL */}
      {rejectModalOpen && selectedRequestForReject && (
        <div className="std-modal-overlay">
          <div className="std-modal-card" style={{ maxWidth: "420px" }}>
            <div className="std-modal-header" style={{ padding: "10px 16px" }}>
              <h3 className="std-modal-title" style={{ color: "#b91c1c", fontSize: "14px", margin: 0, display: "flex", alignItems: "center", gap: "6px" }}>
                <XCircle size={16} color="#dc2626" />
                Reject Registration Request
              </h3>
              <button className="std-modal-close" onClick={handleCloseRejectModal}>
                <X size={15} />
              </button>
            </div>
            <div className="std-modal-body" style={{ padding: "12px 16px" }}>
              <p style={{ fontSize: "12.5px", color: "#334155", margin: "0 0 10px" }}>
                Are you sure you want to reject the application for{" "}
                <strong>
                  {selectedRequestForReject.first_name} {selectedRequestForReject.last_name}
                </strong>?
              </p>
              <div>
                <label className="std-field-label" style={{ marginBottom: "4px", fontSize: "11.5px" }}>
                  Rejection Reason (Optional)
                </label>
                <textarea
                  rows={2}
                  value={rejectionReasonInput}
                  onChange={(e) => setRejectionReasonInput(e.target.value)}
                  placeholder="e.g. Batch is currently full..."
                  className="std-field-input"
                  style={{ width: "100%", padding: "6px 8px", fontSize: "12px", resize: "vertical" }}
                />
              </div>
            </div>
            <div
              className="std-modal-footer"
              style={{ padding: "8px 16px", display: "flex", justifyContent: "flex-end", gap: "6px" }}
            >
              <button
                type="button"
                className="std-btn-cancel"
                onClick={handleCloseRejectModal}
                style={{ padding: "5px 12px", fontSize: "12px" }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitReject}
                disabled={actionLoadingId === selectedRequestForReject.id}
                style={{
                  padding: "5px 14px",
                  borderRadius: "5px",
                  background: "#dc2626",
                  color: "#ffffff",
                  border: "none",
                  fontWeight: "600",
                  fontSize: "12px",
                  cursor: "pointer",
                }}
              >
                {actionLoadingId === selectedRequestForReject.id ? "Rejecting..." : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
